import { compact } from 'lodash';
import React, { Ref, RefObject, useContext, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  MenuItem,
  MenubarContext,
  NavigationContext,
  UserBtnAction,
  UserContext,
  useWebsocketEffect,
} from '@eas/common-web';
import { EvidenceBrowserUrl } from '../../enums/evidence-url';
import { Me } from '../../models';
import { Permission } from '@enums';

export type TAPISMenuItem = MenuItem & {
  permission?: string[];
  items?: TAPISMenuItem[];
};

export function useMenuItems(menuRef: Ref<MenubarContext>) {
  const { navigate } = useContext(NavigationContext);
  const { user, hasPermission, reload } =
    useContext<UserContext<Me>>(UserContext);

  useWebsocketEffect(() => {
    reload();
    // console.log('message SESSION_REFRESHED');
  }, ['SESSION_REFRESHED']);

  const items: TAPISMenuItem[] = compact([
    // Datová základna
    {
      label: (
        <FormattedMessage
          id="TAPIS__MENU__DATA_BASE"
          defaultMessage="Datová základna"
        />
      ),
      isActive: (pathname) => {
        return !![
          EvidenceBrowserUrl.DATA_SOURCE,
          EvidenceBrowserUrl.IMPORT_REQUEST,
          EvidenceBrowserUrl.NON_STANDARD_STATE,
          EvidenceBrowserUrl.SCHEDULE_JOBS,
        ].find((url) => pathname.includes(url));
      },
      permission: [Permission.Default.ADMINISTRATOR],
      items: [
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__DATA_SOURCE"
              defaultMessage="Správa zdrojů informací"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.DATA_SOURCE),
          href: EvidenceBrowserUrl.DATA_SOURCE,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__IMPORT_REQUEST"
              defaultMessage="Přehled importů"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.IMPORT_REQUEST),
          href: EvidenceBrowserUrl.IMPORT_REQUEST,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__DICTIONARIES__NON_STANDARD_STATES"
              defaultMessage="Přehled nestandardních stavů"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.NON_STANDARD_STATE),
          href: EvidenceBrowserUrl.NON_STANDARD_STATE,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__DICTIONARIES__SCHEDULE_JOBS"
              defaultMessage="Přehled plánovaných úloh"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.SCHEDULE_JOBS),
          href: EvidenceBrowserUrl.SCHEDULE_JOBS,
        },
      ],
    },

    // Správa systému
    {
      label: (
        <FormattedMessage
          id="TAPIS__MENU__SYSTEM_MANAGEMENT"
          defaultMessage="Správa systému"
        />
      ),
      isActive: (pathname) => {
        return !![
          EvidenceBrowserUrl.PARAGRAPH,
          EvidenceBrowserUrl.IP_ENTRY,
          EvidenceBrowserUrl.MPT,
          EvidenceBrowserUrl.INTERNAL_SETTINGS,
          EvidenceBrowserUrl.ALOG,
        ].find((url) => pathname.includes(url));
      },
      permission: [Permission.Default.ADMINISTRATOR],
      items: [
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__PARAGRAPH"
              defaultMessage="Správa textací"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.PARAGRAPH),
          href: EvidenceBrowserUrl.PARAGRAPH,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__IP_ENTRIES"
              defaultMessage="Správa blokací IP Adres"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.IP_ENTRY),
          href: EvidenceBrowserUrl.IP_ENTRY,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__MPT"
              defaultMessage="Správa MPT"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.MPT),
          href: EvidenceBrowserUrl.MPT,
        },
        {
          label: (
            <FormattedMessage
              id="IP__MENU__INTERNAL_SETTINGS"
              defaultMessage="Konfigurace aplikace"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.INTERNAL_SETTINGS),
          href: EvidenceBrowserUrl.INTERNAL_SETTINGS,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__GDPR_DATA"
              defaultMessage="Auditní log"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.ALOG),
          href: EvidenceBrowserUrl.ALOG,
        },
      ],
    },

    // Záznamy dotazů
    {
      label: (
        <FormattedMessage
          id="TAPIS__MENU__RESEARCH_QUERY"
          defaultMessage="Záznamy dotazů"
        />
      ),
      onClick: () => navigate(EvidenceBrowserUrl.RESEARCH_QUERY),
      isActive: (pathname) => {
        return !![EvidenceBrowserUrl.RESEARCH_QUERY].find((url) =>
          pathname.includes(url)
        );
      },
      href: EvidenceBrowserUrl.RESEARCH_QUERY,
      permission: [Permission.Default.ADMINISTRATOR],
    },

    // Přístupové účty
    {
      label: (
        <FormattedMessage
          id="TAPIS__MENU__ACCESS_ACCOUNTS"
          defaultMessage="Přístupové účty"
        />
      ),
      isActive: (pathname) => {
        return !![EvidenceBrowserUrl.SYSTEM_USERS].find((url) =>
          pathname.includes(url)
        );
      },
      permission: [Permission.Default.ADMINISTRATOR],
      items: [
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__EXTERNAL_USERS"
              defaultMessage="Přehled uživatelů"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.USERS),
          href: EvidenceBrowserUrl.USERS,
        },
        {
          label: (
            <FormattedMessage
              id="TAPIS__MENU__SYSTEM_USERS"
              defaultMessage="Správa systémových uživatelů"
            />
          ),
          onClick: () => navigate(EvidenceBrowserUrl.SYSTEM_USERS),
          href: EvidenceBrowserUrl.SYSTEM_USERS,
        },
      ],
    },

    // Vlastní vývojové nástroje (zobrazeny pouze na nasazene verzi)
    ...(process.env.NODE_ENV === 'production'
      ? [
          {
            label: (
              <FormattedMessage
                id="TAPIS__MENU__CUSTOM_DEVELOPER_TOOLS"
                defaultMessage="Vývojové nástroje"
              />
            ),
            isActive: (pathname: string) => {
              return !![EvidenceBrowserUrl.CUSTOM_DEVELOPER_TOOLS].find((url) =>
                pathname.includes(url)
              );
            },
            permission: [Permission.Default.ADMINISTRATOR],
            items: [
              {
                label: (
                  <FormattedMessage
                    id="TAPIS__MENU__CUSTOM_DEVELOPER_TOOLS__REINDEX"
                    defaultMessage="Reindex"
                  />
                ),
                onClick: () =>
                  navigate(EvidenceBrowserUrl.CUSTOM_DEVELOPER_TOOLS),
                href: EvidenceBrowserUrl.CUSTOM_DEVELOPER_TOOLS,
              },
            ],
          },
        ]
      : []),
    // {
    //   label: (
    //     <FormattedMessage
    //       id="TAPIS__MENU__SETTINGS"
    //       defaultMessage="Nastavení"
    //     />
    //   ),
    //   isActive: (pathname) => {
    //     return !![
    //       EvidenceBrowserUrl.SEQUENCES,
    //       EvidenceBrowserUrl.SCHEDULE_JOBS,
    //       EvidenceBrowserUrl.SCHEDULE_RUNS,
    //       EvidenceBrowserUrl.EXPORT_REQUESTS,
    //       EvidenceBrowserUrl.EXPORT_TEMPLATES,
    //       EvidenceBrowserUrl.ADMIN_SOAP_MESSAGES,
    //       EvidenceBrowserUrl.APP_SETTINGS,
    //     ].find((url) => pathname.includes(url));
    //   },
    //   permission: [Permission.Default.ADMINISTRATOR],
    //   items: [
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__EXPORT_TEMPLATES"
    //           defaultMessage="Šablony pro export"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.EXPORT_TEMPLATES),
    //       href: EvidenceBrowserUrl.EXPORT_TEMPLATES,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__EXPORT_QUEUE"
    //           defaultMessage="Fronta exportů"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.EXPORT_REQUESTS),
    //       href: EvidenceBrowserUrl.EXPORT_REQUESTS,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__DICTIONARIES__SEQUENCES"
    //           defaultMessage="Číselné řady"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.SEQUENCES),
    //       href: EvidenceBrowserUrl.SEQUENCES,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__DICTIONARIES__SCHEDULE_JOBS"
    //           defaultMessage="Časové úlohy"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.SCHEDULE_JOBS),
    //       href: EvidenceBrowserUrl.SCHEDULE_JOBS,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__DICTIONARIES__SCHEDULE_RUNS"
    //           defaultMessage="Běhy časových úloh"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.SCHEDULE_RUNS),
    //       href: EvidenceBrowserUrl.SCHEDULE_RUNS,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__AUDIT__SOAP_MESSAGES"
    //           defaultMessage="Systémová komunikace (SOAP zprávy)"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.ADMIN_SOAP_MESSAGES),
    //       href: EvidenceBrowserUrl.ADMIN_SOAP_MESSAGES,
    //     },
    //     {
    //       label: (
    //         <FormattedMessage
    //           id="TAPIS__MENU__APP_SETTINGS"
    //           defaultMessage="Další nastavení aplikace"
    //         />
    //       ),
    //       onClick: () => navigate(EvidenceBrowserUrl.APP_SETTINGS),
    //       href: EvidenceBrowserUrl.APP_SETTINGS,
    //     },
    //   ],
    // },
  ]);

  const userBtnActions: UserBtnAction[] = useMemo(
    () => [
      /*    {
        label: (
          <FormattedMessage id="TAPIS__MENU__PROFILE" defaultMessage="Profil" />
        ),
        action: () => {
          navigate('profil');
        },
      }, */
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (menuRef) {
      const filteredItems = filterItems(items);

      (menuRef as RefObject<MenubarContext>)?.current?.modifyItems(
        () => filteredItems
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const filterItems = (items: TAPISMenuItem[]) => {
    if (!user) {
      return [];
    }

    const getNodes = (result: TAPISMenuItem[], item: TAPISMenuItem) => {
      if (!item.items && !item.permission) {
        result.push(item);
        return result;
      }

      const permitted = (item?.permission ?? []).some((perm) => {
        return hasPermission(perm);
      });

      if (permitted) {
        result.push(item);
        return result;
      }

      if (Array.isArray(item.items)) {
        const items = item.items.reduce(getNodes, []);
        if (items.length) result.push({ ...item, items });
      }

      return result;
    };

    return items.reduce(getNodes, []);
  };

  const filteredItems = filterItems(items);

  return { items, userBtnActions, filteredItems };
}
